import { EllipsisOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, notification, Radio, Tag } from "antd";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AttributeEditForm } from "@/lib/theme/components/AttributeEditForm";
import { EditableCustomerAttribute } from "@/lib/theme/components/EditableCustomerAttribute";
import {
  TAX_ADVISORY_PERMISSION_SCOPES,
  PERMISSION_SCOPES,
  useAuth,
} from "@/modules/auth";
import { useGetNotesQuery } from "../../../../../api/graphql/queries/notes";
import {
  IUser,
  IUserDetails,
  KontaxUserStatus,
  KontaxUserStatusAdditionalParams,
  OnbStatus,
  PaymentFrequency,
  YesNoEnum,
} from "../../../../../types";
import {
  formatBooleanToString,
  getColorByOnboardingStatus,
} from "../../../../../utils";
import { getUserFullName } from "../../../../../utils/getUserFullName";

import type { EditableAccountAttributes } from "@/v1/types/IAccount.type";
import { DocumentIntakeEmail } from "@/modules/integrations/documents/components/DocumentIntakeEmail";
import ActionLogDrawerAsync from "../../../../common/ActionLogDrawerAsync";
import CustomerAttribute from "../../../../common/CustomerAttribute";
import EditableCustomerAttributeV1, {
  EditType,
} from "../../../../common/EditableCustomerAttribute";
import {
  CustomerAttributes,
  CustomerInformationCard,
  CustomerInformationSection,
  CustomerInformationSectionTitle,
  CustomerInformationSectionTitleWrapper,
  SectionTitle,
} from "../../../../common/styledComponents";
import { DateInputType } from "../../../../common/TransactionFilters/DateInput";
import UserInfoDrawer, {
  UserInfoDrawerSources,
} from "../../../../common/UserInfoExcerpt/UserInfoDrawer";
import UserTaxInformationBlock from "../../../../common/UserTaxInformationBlock";
import UserBusinessInformationBlock from "../../Common/UserDetails/BusinessInformation";
import { ChangeKontaxStatusButton } from "../../Common/UserDetails/ChangeKontaxStatusButton";
import CustomerStatus from "../../Common/UserDetails/CustomerStatus";
import DependentsTable from "../../Common/UserDetails/DependentTable/DependentsTable";
import {
  ButtonsWrapper,
  HorizontalLine,
} from "../../Common/UserDetails/styledComponents";
import UserTaxNumbersList from "../../Common/UserDetails/TaxNumberList/TaxNumbersList";
import {
  formatDayMonthYear,
  formatMonthYear,
  formatOnbStatus,
} from "../../Common/UserDetails/utils";
import { EditAddressForm } from "./EditAddressForm";
import { EditNameForm } from "./EditNameForm";
import { UserFilterOptions } from "../../Common/UserFilters";
import ClientAssignButton from "./ClientAssignButton";
import AssignClientModal from "./AssignClientModal";
import { getRequiredSteuerIdRules } from "@/v1/utils/steuerId";
import { COUNTRY_NAMES } from "@/v1/constants/countryNames";
import { DISPLAY_DATE_FORMAT, SAVE_DATE_FORMAT } from "@/v1/constants";
import { getLastDeclarationDueDate } from "@/v1/components/common/helpers";
import { getFibuStartDateSaveValue } from "./getFibuStartDateSaveValue";
import { FIRM_LABELS, FIRMS, type Firm } from "@/modules/firms";
import { updateCustomerFirm } from "@/modules/integrations/accounts";

const languageOptions = [
  { label: "Deutsch", value: "de" },
  { label: "English", value: "en" },
];

export function CustomerInformation({
  user,
  editUser,
  editAccount,
  updateUserStatus,
  updateDependents,
  deleteDependent,
  setEmailParam,
  setFilterOptions,
  refreshUser,
}: {
  user: IUser;
  editUser: (email: string, userDetails: IUserDetails) => Promise<void>;
  editAccount: (
    id: number,
    accountDetails: EditableAccountAttributes
  ) => Promise<void>;
  updateUserStatus: (
    email: string,
    status: KontaxUserStatus | null,
    additionalParams?: KontaxUserStatusAdditionalParams
  ) => Promise<void>;
  updateDependents: Function;
  deleteDependent: (email: string, id: string) => Promise<void>;
  setEmailParam: (email: string) => void;
  setFilterOptions: (options: UserFilterOptions) => void;
  refreshUser: (email: string) => Promise<void>;
}) {
  const { i18n, t } = useTranslation();
  const countryLang = i18n.language.startsWith("de") ? "de" : "en";
  const countryLabel = user.country
    ? COUNTRY_NAMES[user.country]?.[countryLang] ?? user.country
    : "";

  const [isUserInfoDrawerVisible, setIsUserInfoDrawerVisible] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const { hasOperatorPermissionScope } = useAuth();

  const hasTaxAdvisoryPermissionScope = hasOperatorPermissionScope(
    TAX_ADVISORY_PERMISSION_SCOPES
  );
  const canSelfAssign = hasOperatorPermissionScope([
    PERMISSION_SCOPES.TAX_SUPERVISOR,
    PERMISSION_SCOPES.ADMIN,
    PERMISSION_SCOPES.CLERK,
  ]);

  const canAssignOnBehalf = hasOperatorPermissionScope([
    PERMISSION_SCOPES.TAX_SUPERVISOR,
    PERMISSION_SCOPES.ADMIN,
  ]);

  const { data: notes, error: getNotesError } = useGetNotesQuery({
    variables: { email: user.email },
    skip: !hasTaxAdvisoryPermissionScope,
  });

  useEffect(() => {
    if (getNotesError) {
      notification.error({
        message: `An error occurred while fetching the notes for the user ${user.email}`,
      });
    }
  }, [getNotesError, user.email]);

  const onSaveFibuEndDateHandler = (fibuEndDate: Date | null) => {
    const lastDeclarationDueDate = getLastDeclarationDueDate(
      fibuEndDate,
      user.vatPaymentFrequency as PaymentFrequency
    );
    editUser(user.email, {
      kontaxUser: {
        lastDeclarationDueDate,
      },
    });
  };

  const onSaveKontaxOnbStatusHandler = (onbStatus: OnbStatus) =>
    editUser(user.email, {
      kontaxUser: {
        onbStatus: onbStatus === OnbStatus.NOT_SET ? null : onbStatus,
      },
    });

  const onSaveTaxDeclarationStartDateHandler = async (
    firstTaxDeclarationDate: Moment | null
  ) => {
    const year =
      firstTaxDeclarationDate === null ? null : firstTaxDeclarationDate.year();
    await editUser(user.email, {
      firstTaxDeclarationYear: year,
    });
  };

  const onSaveTaxDeclarationEndDateHandler = async (
    lastTaxDeclarationDate: Moment | null
  ) => {
    const year =
      lastTaxDeclarationDate === null ? null : lastTaxDeclarationDate.year();
    await editUser(user.email, {
      lastTaxDeclarationYear: year,
    });
  };

  const terminationRequestedAt = user.terminationRequestedAt
    ? moment.utc(user.terminationRequestedAt)
    : null;

  return (
    <CustomerInformationSection data-test="customerDetails">
      <UserInfoDrawer
        visible={isUserInfoDrawerVisible}
        onClose={() => setIsUserInfoDrawerVisible(false)}
        user={user}
        notes={notes?.getNotes || []}
        source={UserInfoDrawerSources.ACCOUNTING}
      />
      <CustomerInformationSectionTitleWrapper>
        <CustomerInformationSectionTitle>
          Customer Information
        </CustomerInformationSectionTitle>
        {terminationRequestedAt &&
          (terminationRequestedAt.isAfter(moment()) ? (
            <Tag color="orange">
              Access valid until{" "}
              {terminationRequestedAt
                .clone()
                .subtract(1, "day")
                .format(DISPLAY_DATE_FORMAT)}
            </Tag>
          ) : (
            <Tag color="red">
              Access terminated on{" "}
              {terminationRequestedAt.format(DISPLAY_DATE_FORMAT)}
            </Tag>
          ))}
        {canSelfAssign && (
          <ClientAssignButton
            user={user}
            onSuccess={() => refreshUser(user.email)}
          />
        )}
        {canAssignOnBehalf && (
          <Button type="default" onClick={() => setIsAssignModalOpen(true)}>
            {t("assignClient") || "Assign client"}
          </Button>
        )}
        <Button
          type="default"
          onClick={() => setIsUserInfoDrawerVisible(true)}
          icon={<EllipsisOutlined />}
        >
          More info
        </Button>
      </CustomerInformationSectionTitleWrapper>
      <AssignClientModal
        user={user}
        open={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={() => refreshUser(user.email)}
      />
      <CustomerInformationCard>
        <CustomerAttributes>
          <EditableCustomerAttribute
            label="Name"
            dataTest="name"
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="View Logs"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["firstName", "lastName"]}
              />
            }
            renderEditForm={(onDone: () => void) => (
              <EditNameForm
                initialValues={{
                  firstName: user.firstName,
                  lastName: user.lastName,
                }}
                onFinish={async ({ firstName, lastName }) => {
                  await editUser(user.email, {
                    firstName,
                    lastName,
                  });
                  onDone();
                }}
                onReset={onDone}
              />
            )}
          >
            {getUserFullName(user)}
          </EditableCustomerAttribute>

          <CustomerAttribute
            label="Status"
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="user"
                recordIds={[`${user.id}`]}
                small
                selectedFields={["kontaxStatus"]}
              />
            }
          >
            <CustomerStatus status={user.kontaxStatus} />
          </CustomerAttribute>
          <SectionTitle>
            <EditableCustomerAttributeV1
              label="ONB Status"
              dataTest="onbStatus"
              editType={EditType.Select}
              formatFunction={formatOnbStatus}
              options={Object.values(OnbStatus)}
              initialValue={user.kontaxUser?.onbStatus || OnbStatus.NOT_SET}
              onSaveHandler={onSaveKontaxOnbStatusHandler}
              renderAttributeContent={(value: OnbStatus) => {
                return (
                  <Tag color={getColorByOnboardingStatus(value)}>
                    {formatOnbStatus(value).toUpperCase()}
                  </Tag>
                );
              }}
              inputRequired
              allowClear
              changeLog={
                <ActionLogDrawerAsync
                  title="Action log"
                  modelName="kontax_user"
                  recordIds={[`${user.kontaxUser?.id}`]}
                  small
                  selectedFields={["onbStatus"]}
                />
              }
              isReadOnly={!hasTaxAdvisoryPermissionScope}
            />
          </SectionTitle>
          <EditableCustomerAttribute
            label="Firmenname"
            dataTest="businessTradingName"
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="View Logs"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["businessTradingName"]}
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="businessTradingName"
                initialValue={user.businessTradingName}
                onFinish={async (value) => {
                  await editUser(user.email, {
                    businessTradingName: value || null,
                  });
                  onDone();
                }}
                onReset={onDone}
              >
                <Input type="text" placeholder="Firmenname" autoFocus />
              </AttributeEditForm>
            )}
          >
            {user.businessTradingName || "-"}
          </EditableCustomerAttribute>
          <CustomerAttribute
            label="Public ID"
            dataTest="publicId"
            isCopyable
            withFixedIcon
          >
            {user.publicId}
          </CustomerAttribute>
          <CustomerAttribute label="Last Login At">
            {formatDayMonthYear(user.lastLoginAt)}
          </CustomerAttribute>
          {/*
           * Rufname / Customer Email sit in column 1. Account ID and Firm fill
           * columns 2-3, while smarta Receipts Email spans those columns below.
           */}
          <EditableCustomerAttribute
            label="Rufname"
            dataTest="preferredName"
            style={{ gridColumn: 1 }}
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="View Logs"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["preferredName"]}
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="preferredName"
                initialValue={user.preferredName}
                onFinish={async (value) => {
                  await editUser(user.email, {
                    preferredName: value,
                  });
                  onDone();
                }}
                onReset={onDone}
              >
                <Input type="text" placeholder="Rufname" autoFocus />
              </AttributeEditForm>
            )}
          >
            {user.preferredName || "-"}
          </EditableCustomerAttribute>
          <CustomerAttribute
            label="Account ID"
            dataTest="accountId"
            style={{ gridColumn: 2 }}
            isCopyable
            withFixedIcon
          >
            {user.accountId}
          </CustomerAttribute>
          <EditableCustomerAttributeV1
            label={t("firm")}
            dataTest="firm"
            editType={EditType.Select}
            options={[...FIRMS]}
            formatFunction={(firm: Firm) => FIRM_LABELS[firm]}
            initialValue={user.firm ?? "smarta"}
            onSaveHandler={async (firm: Firm) => {
              await updateCustomerFirm(user.publicId, firm);
              await refreshUser(user.email);
            }}
            isReadOnly={!canAssignOnBehalf}
            inputRequired
          />
          {/*
           * Customer Email + smarta Receipts Email occupy the next row, below
           * Rufname / Account ID and above FiBu onboarding. The smarta receipts
           * email is the customer's email-forwarding intake address
           * (receipts-{token}@smarta.one), fetched via the typed smarta-documents
           * integration client.
           */}
          <EditableCustomerAttribute
            label="Customer Email"
            dataTest="email"
            style={{ gridColumn: 1 }}
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="View Logs"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["email"]}
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="email"
                initialValue={user.email}
                onFinish={async (value) => {
                  if (value !== user.email) {
                    await editUser(user.email, {
                      newEmail: value,
                    });

                    setEmailParam(value);
                    setFilterOptions({
                      search: value,
                      all: false,
                      firm: user.firm ?? "smarta",
                    });
                    onDone();
                  }
                }}
                rules={[
                  {
                    required: true,
                    message: "Email is required",
                  },
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input type="text" placeholder="Email" autoFocus />
              </AttributeEditForm>
            )}
          >
            {user.email}
          </EditableCustomerAttribute>
          <DocumentIntakeEmail
            customerId={user.publicId}
            style={{ gridColumn: "2 / span 2" }}
          />
          <EditableCustomerAttributeV1
            label="FiBu onboarding completed"
            dataTest="FiBuOnboardingCompleted"
            initialValue={formatBooleanToString(
              !!user.taxServiceOnboardingCompletedAt
            )}
            editType={EditType.Select}
            options={[...Object.values(YesNoEnum)]}
            onSaveHandler={async (value: YesNoEnum) => {
              const taxServiceOnboardingCompleted = value === YesNoEnum.YES;

              await editUser(user.email, {
                taxServiceOnboardingCompleted,
              });
            }}
            isReadOnly={!hasTaxAdvisoryPermissionScope}
          />
          <EditableCustomerAttribute
            label="FIBU start date"
            dataTest="fibuStartDate"
            isReadOnly={!hasTaxAdvisoryPermissionScope}
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="fibuStartDate"
                initialValue={
                  user.fibuStartDate ? moment(user.fibuStartDate) : undefined
                }
                onFinish={async (value) => {
                  // A FiBu end date without a start date makes no sense, so the
                  // start date can only be cleared once the end date is gone.
                  if (!value && user.kontaxUser?.lastDeclarationDueDate) {
                    notification.error({
                      message: "Can't clear FiBu start date",
                      description:
                        "A FiBu end date is still set. Remove the FiBu end date first.",
                    });
                    return;
                  }
                  await editUser(user.email, {
                    fibuStartDate: getFibuStartDateSaveValue(value),
                  });
                  onDone();
                }}
                onReset={onDone}
                dataTest="fibuStartDateEdit"
              >
                <DatePicker picker="month" allowClear />
              </AttributeEditForm>
            )}
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["fibuStartDate"]}
                small
              />
            }
          >
            {formatMonthYear(user.fibuStartDate)}
          </EditableCustomerAttribute>
          <EditableCustomerAttributeV1
            label="FIBU end date"
            initialValue={user.kontaxUser?.lastDeclarationDueDate}
            formatFunction={formatMonthYear}
            editType={EditType.PeriodPicker}
            allowClear
            onSaveHandler={onSaveFibuEndDateHandler}
            vatPaymentFrequency={user.vatPaymentFrequency as PaymentFrequency}
            isReadOnly={!hasTaxAdvisoryPermissionScope}
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="kontax_user"
                recordIds={[`${user.kontaxUser?.id}`]}
                selectedFields={["lastDeclarationDueDate"]}
                small
              />
            }
          />
          <EditableCustomerAttributeV1
            label="Erste Steuererklärung"
            dataTest="ersteSteuer"
            initialValue={user.firstTaxDeclarationYear?.toString()}
            editType={DateInputType.Year}
            allowClear
            onSaveHandler={onSaveTaxDeclarationStartDateHandler}
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["firstTaxDeclarationYear"]}
                small
              />
            }
            isReadOnly={!hasTaxAdvisoryPermissionScope}
          />
          <EditableCustomerAttributeV1
            label="Letzte Steuererklärung"
            initialValue={user.lastTaxDeclarationYear?.toString()}
            editType={DateInputType.Year}
            allowClear
            onSaveHandler={onSaveTaxDeclarationEndDateHandler}
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["lastTaxDeclarationYear"]}
                small
              />
            }
            isReadOnly={!hasTaxAdvisoryPermissionScope}
          />
        </CustomerAttributes>
      </CustomerInformationCard>

      <HorizontalLine />

      <UserTaxNumbersList user={user} />
      <HorizontalLine />

      <UserTaxInformationBlock user={user} editUser={editUser} />
      <HorizontalLine />

      <CustomerInformationSectionTitle>
        Personal Information
      </CustomerInformationSectionTitle>
      <CustomerInformationCard>
        <CustomerAttributes>
          <EditableCustomerAttribute
            label="Phone number"
            dataTest="phoneNumber"
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="View Logs"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["mobileNumber", "untrustedPhoneNumber"]}
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="phoneNumber"
                initialValue={user.mobileNumber || user.untrustedPhoneNumber}
                rules={[
                  {
                    required: true,
                    message: "Phone number is required",
                  },
                  {
                    // should match all international phone numbers
                    pattern: /^\+[1-9]\d{6,14}$/,
                    message:
                      "Phone number must start with a '+' followed by 7 to 15 digits",
                  },
                  {
                    // should not match Ukrainian phone numbers
                    pattern: /^(?!\+380)\+[1-9]\d{6,14}$/,
                    message: "Phone number is not allowed",
                  },
                ]}
                onFinish={async (value) => {
                  await editUser(user.email, {
                    mobileNumber: value,
                  });
                  onDone();
                }}
                onReset={onDone}
              >
                <Input type="text" placeholder="Phone number" autoFocus />
              </AttributeEditForm>
            )}
          >
            {user.mobileNumber || user.untrustedPhoneNumber}
          </EditableCustomerAttribute>
          <CustomerAttribute label="Email address" isCopyable withFixedIcon>
            {user.email}
          </CustomerAttribute>
          <EditableCustomerAttribute
            label="Language"
            dataTest="language"
            changeLog={
              <ActionLogDrawerAsync
                title="View Logs"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["language"]}
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="language"
                initialValue={user.language}
                onFinish={async (language) => {
                  await editUser(user.email, { language });
                  onDone();
                }}
                onReset={onDone}
              >
                <Radio.Group options={languageOptions} />
              </AttributeEditForm>
            )}
          >
            {
              languageOptions.find((option) => option.value === user.language)
                ?.label
            }
          </EditableCustomerAttribute>
          <EditableCustomerAttribute
            label="Geburtstag"
            dataTest="birthDate"
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["birthDate"]}
                small
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="birthDate"
                initialValue={
                  user.birthDate ? moment(user.birthDate) : undefined
                }
                rules={[
                  {
                    required: true,
                    message: "Birth date is required",
                  },
                ]}
                onFinish={async (momentDate) => {
                  await editUser(user.email, {
                    birthDate: momentDate?.format(SAVE_DATE_FORMAT),
                  });
                  onDone();
                }}
                onReset={onDone}
              >
                <DatePicker
                  placeholder="Eg. 01.01.1990"
                  format="DD.MM.YYYY"
                  disabledDate={(date) => date.isAfter(moment(), "day")}
                />
              </AttributeEditForm>
            )}
          >
            {formatDayMonthYear(user.birthDate)}
          </EditableCustomerAttribute>
          <div>{/* Shifting */}</div>
          <EditableCustomerAttribute
            label="Address"
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["street", "postCode", "city", "country"]}
                small
              />
            }
            isCopyable
            withFixedIcon
            renderEditForm={(onDone: () => void) => (
              <EditAddressForm
                initialValues={{
                  street: user.street,
                  postCode: user.postCode,
                  city: user.city,
                  country: user.country,
                }}
                onFinish={async (address) => {
                  await editUser(user.email, {
                    street: address.street,
                    postCode: address.postCode,
                    city: address.city,
                    country: address.country,
                  });
                  onDone();
                }}
                onReset={onDone}
              />
            )}
          >
            {`${user.street}, ${user.postCode} ${user.city}${
              countryLabel ? `, ${countryLabel}` : ""
            }`}
          </EditableCustomerAttribute>
          <EditableCustomerAttribute
            label="Steuerliche Identifikationsnummer"
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="user"
                recordIds={[`${user.id}`]}
                selectedFields={["deTaxId"]}
                small
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="deTaxId"
                initialValue={user.deTaxId}
                rules={getRequiredSteuerIdRules(t)}
                onFinish={async (deTaxId) => {
                  await editUser(user.email, { deTaxId });
                  onDone();
                }}
                onReset={onDone}
              >
                <Input
                  type="text"
                  placeholder="Steuerliche Identifikationsnummer"
                  maxLength={11}
                />
              </AttributeEditForm>
            )}
          >
            {user.deTaxId}
          </EditableCustomerAttribute>
          <EditableCustomerAttribute
            label="IBAN"
            isCopyable
            withFixedIcon
            changeLog={
              <ActionLogDrawerAsync
                title="Action log"
                modelName="account"
                recordIds={[user.accountId.toString()]}
                selectedFields={["iban"]}
                small
              />
            }
            renderEditForm={(onDone: () => void) => (
              <AttributeEditForm
                name="accountIban"
                initialValue={user.accountIban}
                onFinish={async (iban) => {
                  await editAccount(user.accountId, {
                    iban: iban || null,
                  });
                  onDone();
                }}
                onReset={onDone}
                rules={[
                  {
                    // IBAN pattern: 2 letter country code followed by up to 32 alphanumeric chars
                    pattern: /^[A-Z]{2}[0-9A-Z]{2,32}$/,
                    message: "Invalid IBAN",
                  },
                ]}
              >
                <Input
                  type="text"
                  placeholder="eg. DE75512108001245126199"
                  allowClear
                />
              </AttributeEditForm>
            )}
          >
            {user.accountIban}
          </EditableCustomerAttribute>
        </CustomerAttributes>
      </CustomerInformationCard>
      <HorizontalLine />

      <UserBusinessInformationBlock user={user} editUser={editUser} />
      <HorizontalLine />

      <CustomerInformationSectionTitle>
        User's Dependents
      </CustomerInformationSectionTitle>
      <DependentsTable
        email={user.email}
        dependents={user.dependents || []}
        updateDependents={updateDependents}
        deleteDependent={deleteDependent}
      />
      <HorizontalLine />

      {hasTaxAdvisoryPermissionScope && (
        <ButtonsWrapper>
          <ChangeKontaxStatusButton
            kontaxStatus={user.kontaxStatus}
            email={user.email}
            updateUserStatus={updateUserStatus}
          />
        </ButtonsWrapper>
      )}
    </CustomerInformationSection>
  );
}
